import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Loader2, DollarSign, User, MapPin, ClipboardList } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import AddressAutocomplete from '../ui/AddressAutocomplete';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { SERVICE_CATEGORIES } from './LeadCRM';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const blankItem = () => ({ description: '', quantity: 1, unit: 'ea', unit_price: 0 });

const currency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n || 0));

const NewLineItemEstimate = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  const [form, setForm] = useState({
    service_category: 'home_renovations',
    customer_first_name: '',
    customer_last_name: '',
    customer_email: '',
    customer_phone: '',
    property_address: '',
    scope_of_work: '',
    line_items: [blankItem(), blankItem(), blankItem()],
    labor_hours: 0,
    labor_rate: 75,
    tax_rate: 0,
    discount_percent: 0,
    notes: '',
    lead_id: null,
  });

  // Preload from query params (e.g., coming from a CRM lead card)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const get = (k) => {
      const v = params.get(k);
      return v && v !== 'null' && v !== 'undefined' ? v : '';
    };
    const updates = {};
    if (get('first_name')) updates.customer_first_name = get('first_name');
    if (get('last_name')) updates.customer_last_name = get('last_name');
    if (get('email')) updates.customer_email = get('email');
    if (get('phone')) updates.customer_phone = get('phone');
    if (get('address')) updates.property_address = get('address');
    if (get('service_category')) updates.service_category = get('service_category');
    if (get('lead_id')) updates.lead_id = get('lead_id');
    if (Object.keys(updates).length) setForm(f => ({ ...f, ...updates }));
  }, []);

  // Totals (computed live on the client; server re-computes authoritatively)
  const totals = useMemo(() => {
    const materials = form.line_items.reduce(
      (s, li) => s + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0),
      0
    );
    const labor = (Number(form.labor_hours) || 0) * (Number(form.labor_rate) || 0);
    const subtotal = materials + labor;
    const discount = subtotal * (Number(form.discount_percent) || 0) / 100;
    const taxable = Math.max(subtotal - discount, 0);
    const tax = taxable * (Number(form.tax_rate) || 0) / 100;
    const total = taxable + tax;
    return { materials, labor, subtotal, discount, taxable, tax, total };
  }, [form.line_items, form.labor_hours, form.labor_rate, form.tax_rate, form.discount_percent]);

  const updateField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const updateItem = (idx, field, value) => {
    setForm(f => {
      const items = [...f.line_items];
      items[idx] = { ...items[idx], [field]: field === 'quantity' || field === 'unit_price' ? Number(value) || 0 : value };
      return { ...f, line_items: items };
    });
  };

  const addItem = () => setForm(f => ({ ...f, line_items: [...f.line_items, blankItem()] }));
  const removeItem = (idx) =>
    setForm(f => ({
      ...f,
      line_items: f.line_items.length > 1 ? f.line_items.filter((_, i) => i !== idx) : f.line_items,
    }));

  const handleSave = async () => {
    if (!form.customer_last_name && !form.property_address) {
      toast.error('Add a customer name or property address before saving');
      return;
    }
    const hasAnyItem = form.line_items.some(li => li.description && (li.quantity > 0 || li.unit_price > 0));
    const hasLabor = (form.labor_hours > 0 && form.labor_rate > 0);
    if (!hasAnyItem && !hasLabor) {
      toast.error('Add at least one line item or labor hours');
      return;
    }
    setIsSaving(true);
    try {
      const token = localStorage.getItem('rooftrack_token');
      // Strip empty items server-side-friendly
      const payload = {
        ...form,
        line_items: form.line_items.filter(li => li.description || li.quantity || li.unit_price),
      };
      const res = await axios.post(
        `${BACKEND_URL}/api/line-item-estimates`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Estimate saved!');
      navigate(`/dashboard/estimates/line-item/${res.data.id}`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to save estimate');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="new-line-item-estimate">
      <div className="mb-6">
        <Link to="/dashboard/estimates/new">
          <Button variant="outline" size="sm" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Quick Job Estimate</h1>
        <p className="text-gray-600 mt-1">Line-item estimate for any home service trade</p>
      </div>

      <div className="space-y-6">
        {/* Job Info */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <ClipboardList className="w-5 h-5 mr-2 text-blue-600" />
            Job Details
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Service Category *</Label>
              <select
                required
                value={form.service_category}
                onChange={(e) => updateField('service_category', e.target.value)}
                className="w-full border rounded-md px-3 py-2 mt-1"
                data-testid="lie-service-category"
              >
                {SERVICE_CATEGORIES.map(c => (
                  <option key={c.id} value={c.id}>{c.icon} {c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Scope of Work (short summary)</Label>
              <Input
                value={form.scope_of_work}
                onChange={(e) => updateField('scope_of_work', e.target.value)}
                placeholder="e.g., Full kitchen remodel — cabinets, counters, flooring"
                className="mt-1"
                data-testid="lie-scope"
              />
            </div>
          </div>
        </div>

        {/* Customer */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <User className="w-5 h-5 mr-2 text-blue-600" />
            Customer & Property
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>First Name</Label>
              <Input
                value={form.customer_first_name}
                onChange={(e) => updateField('customer_first_name', e.target.value)}
                className="mt-1"
                data-testid="lie-first-name"
              />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input
                value={form.customer_last_name}
                onChange={(e) => updateField('customer_last_name', e.target.value)}
                className="mt-1"
                data-testid="lie-last-name"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.customer_email}
                onChange={(e) => updateField('customer_email', e.target.value)}
                className="mt-1"
                data-testid="lie-email"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                type="tel"
                value={form.customer_phone}
                onChange={(e) => updateField('customer_phone', e.target.value)}
                className="mt-1"
                data-testid="lie-phone"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="flex items-center"><MapPin className="w-4 h-4 mr-1 text-blue-600" /> Property Address</Label>
              <AddressAutocomplete
                value={form.property_address}
                onChange={(val) => updateField('property_address', val)}
                onSelectAddress={(addr) => updateField('property_address', addr.display_name || addr)}
                placeholder="Start typing an address..."
              />
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-blue-600" />
              Line Items (Materials / Services)
            </h2>
            <Button
              onClick={addItem}
              variant="outline"
              size="sm"
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
              data-testid="lie-add-item-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Line Item
            </Button>
          </div>

          {/* Column headers */}
          <div className="hidden md:grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">
            <div className="col-span-5">Description</div>
            <div className="col-span-2">Qty</div>
            <div className="col-span-1">Unit</div>
            <div className="col-span-2">Unit Price</div>
            <div className="col-span-1 text-right">Subtotal</div>
            <div className="col-span-1"></div>
          </div>

          <div className="space-y-2">
            {form.line_items.map((li, idx) => {
              const sub = (Number(li.quantity) || 0) * (Number(li.unit_price) || 0);
              return (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-2 items-center bg-gray-50 rounded-lg p-2"
                  data-testid={`lie-line-item-${idx}`}
                >
                  <div className="col-span-12 md:col-span-5">
                    <Input
                      placeholder={`Item ${idx + 1} description (e.g., Shaker cabinets - upper & lower)`}
                      value={li.description}
                      onChange={(e) => updateItem(idx, 'description', e.target.value)}
                      data-testid={`lie-item-desc-${idx}`}
                    />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={li.quantity}
                      onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                      data-testid={`lie-item-qty-${idx}`}
                    />
                  </div>
                  <div className="col-span-4 md:col-span-1">
                    <Input
                      placeholder="ea"
                      value={li.unit}
                      onChange={(e) => updateItem(idx, 'unit', e.target.value)}
                      data-testid={`lie-item-unit-${idx}`}
                    />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={li.unit_price}
                      onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                      data-testid={`lie-item-price-${idx}`}
                    />
                  </div>
                  <div className="col-span-10 md:col-span-1 text-right font-semibold text-gray-900">
                    {currency(sub)}
                  </div>
                  <div className="col-span-2 md:col-span-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeItem(idx)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                      aria-label="Remove line item"
                      data-testid={`lie-remove-item-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Labor + Adjustments */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-md font-semibold text-gray-900 mb-4">Labor</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Labor Hours</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.labor_hours}
                  onChange={(e) => updateField('labor_hours', Number(e.target.value) || 0)}
                  className="mt-1"
                  data-testid="lie-labor-hours"
                />
              </div>
              <div>
                <Label>Labor Rate ($/hr)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.labor_rate}
                  onChange={(e) => updateField('labor_rate', Number(e.target.value) || 0)}
                  className="mt-1"
                  data-testid="lie-labor-rate"
                />
              </div>
            </div>
            <div className="mt-3 text-sm text-gray-600">
              Labor subtotal: <span className="font-semibold text-gray-900">{currency(totals.labor)}</span>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-md font-semibold text-gray-900 mb-4">Adjustments</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Discount (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={form.discount_percent}
                  onChange={(e) => updateField('discount_percent', Number(e.target.value) || 0)}
                  className="mt-1"
                  data-testid="lie-discount"
                />
              </div>
              <div>
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={form.tax_rate}
                  onChange={(e) => updateField('tax_rate', Number(e.target.value) || 0)}
                  className="mt-1"
                  data-testid="lie-tax"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <Label>Internal Notes (not shown to customer)</Label>
          <textarea
            value={form.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            className="w-full border rounded-md px-3 py-2 mt-1 h-20"
            placeholder="Anything the team should know about this job..."
            data-testid="lie-notes"
          />
        </div>

        {/* Totals */}
        <div className="bg-gradient-to-br from-blue-600 to-orange-500 rounded-xl shadow-xl p-6 text-white" data-testid="lie-totals">
          <h3 className="text-md font-semibold mb-4 opacity-90">Estimate Summary</h3>
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <div className="opacity-90">Materials / services</div>
            <div className="text-right font-semibold">{currency(totals.materials)}</div>
            <div className="opacity-90">Labor</div>
            <div className="text-right font-semibold">{currency(totals.labor)}</div>
            <div className="opacity-90">Subtotal</div>
            <div className="text-right font-semibold">{currency(totals.subtotal)}</div>
            {totals.discount > 0 && (
              <>
                <div className="opacity-90">Discount ({form.discount_percent}%)</div>
                <div className="text-right font-semibold">-{currency(totals.discount)}</div>
              </>
            )}
            {totals.tax > 0 && (
              <>
                <div className="opacity-90">Tax ({form.tax_rate}%)</div>
                <div className="text-right font-semibold">{currency(totals.tax)}</div>
              </>
            )}
          </div>
          <div className="border-t border-white/30 mt-4 pt-4 flex items-center justify-between">
            <div className="text-lg font-semibold opacity-90">Total</div>
            <div className="text-3xl font-extrabold" data-testid="lie-total-amount">{currency(totals.total)}</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pb-8">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard/estimates')}
            data-testid="lie-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="lie-save-btn"
          >
            {isSaving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save Estimate</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewLineItemEstimate;
